import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

export interface EditTenancyForm {
  rentAmount?: number;
  depositAmount?: number;
  leaseEndDate?: string;
  rentDueDay?: number;
  status?: 'active' | 'inactive';
  rtbRegisteredOn?: string;
}

interface EditTenancyModalProps {
  isOpen: boolean;
  tenantName: string;
  editForm: EditTenancyForm;
  onChange: (updates: Partial<EditTenancyForm>) => void;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
}

const EditTenancyModal: React.FC<EditTenancyModalProps> = ({
  isOpen,
  tenantName,
  editForm,
  onChange,
  onClose,
  onSave,
  saving = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-xl rounded-3xl bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-6 py-4">
          <h3 className="text-xl font-bold text-gray-900">Edit Tenancy</h3>
          <p className="text-sm text-gray-500">
            Update tenancy details for {tenantName}.
          </p>
        </div>
        <div className="px-6 py-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm text-gray-700">Rent Amount (€)</label>
              <input
                type="number"
                value={editForm.rentAmount ?? ''}
                onChange={(e) =>
                  onChange({ rentAmount: Number(e.target.value) })
                }
                className="mt-1 w-full rounded-md border-gray-300 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">Deposit (€)</label>
              <input
                type="number"
                value={editForm.depositAmount ?? ''}
                onChange={(e) =>
                  onChange({ depositAmount: Number(e.target.value) })
                }
                className="mt-1 w-full rounded-md border-gray-300 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">Lease End Date</label>
              <input
                type="date"
                value={editForm.leaseEndDate ?? ''}
                onChange={(e) => onChange({ leaseEndDate: e.target.value })}
                className="mt-1 w-full rounded-md border-gray-300 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">
                Rent Due Day (1-31)
              </label>
              <input
                type="number"
                value={editForm.rentDueDay ?? ''}
                onChange={(e) =>
                  onChange({ rentDueDay: Number(e.target.value) })
                }
                className="mt-1 w-full rounded-md border-gray-300 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">
                RTB Registered Date
              </label>
              <input
                type="date"
                value={editForm.rtbRegisteredOn ?? ''}
                onChange={(e) => onChange({ rtbRegisteredOn: e.target.value })}
                className="mt-1 w-full rounded-md border-gray-300 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm text-gray-700">Status</label>
              <select
                value={editForm.status ?? 'active'}
                onChange={(e) =>
                  onChange({
                    status: e.target.value as 'active' | 'inactive'
                  })
                }
                className="mt-1 w-full rounded-md border-gray-300 px-3 py-2"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-amber-500/40 transition hover:scale-[1.01] disabled:opacity-50"
            >
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTenancyModal;
